import { FC, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { okrApi } from '~src/services/ApiController';
import { translateContent } from '~src/services/translationServices';
import { Uuid } from '~src/services/uuid';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import SelectSearch from '~src/shared/form/select/SelectSearch/SelectSearch';
import {
  FetchSearchSuggestions,
  OptionI,
} from '~src/shared/form/select/SelectSearch/selectSearch.interfaces';
import { ObjectiveItem, SSFilterTypes, SubObjectiveItem } from '~src/store/okrs/interfaces';
import { getUrlFromObject } from '~src/utils';
import { FormTitle } from '~src/modules/okrs/manageObjectives/shared/labels/labelSelectorForm.sc';
import { FormInner, FormContainer, ButtonSection } from '~src/modules/okrs/manageObjectives/create/addObjectiveEntityForm.sc';
import { FieldType } from '~src/types';
import CustomFormInput from '~src/shared/form/inputs/FormInput';

interface Props {
  disallowedIds: string[];
  hide: () => void;
  subobjective?: SubObjectiveItem | null;
  addSubobjective(subObjectiveId: string, weighting: number | null): void;
}

interface FormValues {
  subobjectiveId: string;
  weighting: number | null;
}

const AddSubobjectiveForm: FC<Props> = props => {
  const [t] = useTranslation();

  const submitAddSubobjective = (data: FormValues) => {
    props.addSubobjective(props.subobjective ? props.subobjective.id : data.subobjectiveId, data.weighting);
  };

  const renderInput = useCallback(
    ({ input, label, meta: { touched, error }, labelKey }: FieldType) => (
      <CustomFormInput
        {...input}
        error={touched ? error : null}
        label={label}
        type={input.type}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const fetchSearchSuggestions: FetchSearchSuggestions = async (value: string) => {
    try {
      return [(await okrApi.get(`/objectives/${Uuid.validateAndFormat(value)}`)).data] as ObjectiveItem[];
    } catch (e) {
      const queryString = getUrlFromObject({ [SSFilterTypes.Name]: value });
      return (await okrApi.get(`/objectives${queryString}`)).data.items as SubObjectiveItem[];
    }
  };

  const weightingValueMapper = (value: string) =>
    value === '' || value === null ? null : Math.min(parseInt(value, 10), 100);

  const validate = (values: FormValues) => {
    const errors: Record<string, string> = {};
    if (!values.subobjectiveId) {
      errors.subobjective = 'okr.subobjectives.add.id_empty';
    }

    return errors;
  };

  const mapper = (data: Array<Record<string, any>>) => {
    return data
      .map(i => ({ name: translateContent(i.name), value: i.id }))
      .filter((opt: OptionI) => props.disallowedIds.indexOf(opt.value) === -1);
  };
  const emptyValue = { name: '', value: undefined };

  return (
    <Form
      validate={validate}
      onSubmit={submitAddSubobjective}
      destroyOnUnregister
      initialValues={{
        weighting:
          props.subobjective && props.subobjective.weighting_locked ? props.subobjective.weighting : null,
      }}
    >
      {({ handleSubmit, invalid }) => (
        <FormContainer onSubmit={handleSubmit}>
          <FormInner>
            <FormTitle>
              <span data-translation="x.okr.sub-objectives.add.title">
                {t('x.okr.sub-objectives.add.title')}
              </span>
            </FormTitle>

            {!props.subobjective && (
              <SelectSearch
                t={t}
                labelKey="x.okr.sub-objectives.add.search.placeholder"
                focusedLabelKey="x.okr.sub-objectives.add.search.placeholder"
                inputName="subobjectiveId"
                noSelectedOpt={emptyValue}
                currItemOpt={emptyValue}
                fetchSearchSuggestions={fetchSearchSuggestions}
                mapData={mapper}
              />
            )}
            <Field
              name="weighting"
              label={t('x.okr.add.weighting')}
              onBlur={blur}
              type="number"
              children={renderInput}
              parse={weightingValueMapper}
            />
          </FormInner>
          <ButtonSection>
            <SecondaryButton type="button" onClick={props.hide} data-translation="x.okr.labels.button.cancel">
              {t('x.okr.labels.button.cancel')}
            </SecondaryButton>

            <PrimaryButtonLoading
              isLoading={false}
              data-translation="x.okr.sub-objectives.add.title"
              disabled={invalid}
            >
              {t('x.okr.sub-objectives.add.title')}
            </PrimaryButtonLoading>
          </ButtonSection>
        </FormContainer>
      )}
    </Form>
  );
};

export default AddSubobjectiveForm;
